<script setup>
    
    const props = defineProps({
        session: {
            type: Object,
            required: true
        }
    })

    const activeIndex = ref(0)
    const speakersContainer = ref(null)
    const arrowLeft = ref(0)
    const arrowRight = ref(0)

    const calculateVisibility = () => {
        if (!speakersContainer.value) return;
        if (!props.session.speakers?.length || props.session.speakers.length < 2) return;
        
        const TARGET_PERC = 0.3
        const children = speakersContainer.value.children;
        const containerRect = speakersContainer.value.getBoundingClientRect();
        // Get the last and second to last children
        const lastChild = children[children.length - 1];
        
        let leftmostElement = null;
        let leftmostPosition = Infinity;
        let previousElementVisibility = 0;
        let previousElement = null;

        for (let i = 0; i < children.length; i++) {
            const currentElement = children[i];
            const currentRect = currentElement.getBoundingClientRect();
            
            // Calculate visibility percentage for current element
            let currentVisibleWidth = 0;
            if (currentRect.left < containerRect.right && currentRect.right > containerRect.left) {
                const visibleLeft = Math.max(currentRect.left, containerRect.left);
                const visibleRight = Math.min(currentRect.right, containerRect.right);
                currentVisibleWidth = visibleRight - visibleLeft;
                
                if (currentRect.left < leftmostPosition) {
                    leftmostPosition = currentRect.left;
                    leftmostElement = currentElement;
                }
            }

            let currentVisibilityPercentage = (currentVisibleWidth / currentRect.width);
            let inverseVisibilityPercentage = Math.max(TARGET_PERC, 1 - previousElementVisibility);
            
            // The last child is a special case, since it's previous sibling may never be 100% offscreen.
            if (lastChild === currentElement) { 
                inverseVisibilityPercentage = currentVisibilityPercentage; 
                if (inverseVisibilityPercentage > 0.90) previousElement.style.setProperty('--inverse-visibility', `${TARGET_PERC}`);
                if (currentVisibilityPercentage > 0.90) leftmostElement = currentElement
            }
            
            currentElement.style.setProperty('--current-visibility', `${currentVisibilityPercentage}`);
            currentElement.style.setProperty('--inverse-visibility', `${inverseVisibilityPercentage}`);

            previousElementVisibility = currentVisibilityPercentage;
            previousElement = currentElement;
        }

        if (leftmostElement) {
            activeIndex.value = parseInt(leftmostElement.getAttribute('data-index'), 10)
            const arrowRect = leftmostElement.querySelector('.bio').getBoundingClientRect()
            nextTick(() => {
                if (arrowLeft.value === 0) {
                    arrowLeft.value = arrowRect.left
                    arrowRight.value = arrowRect.right
                }
            })
        }
    }

    const jump = (index) => {
        if (!speakersContainer.value) return;
        const target = document.querySelector(`.speaker[data-index="${index}"]`)
        if (target) {
            const smoothing = /iPad|iPhone|iPod/i.test(navigator.userAgent) ? 'smooth' : 'smooth'
            const offsetLeft = target.offsetLeft - speakersContainer.value.offsetLeft;
            speakersContainer.value.scrollTo({ left: offsetLeft, behavior: smoothing });
        }
    }    

    const next = () => {
        const new_value = activeIndex.value < props.session.speakers.length - 1 ? activeIndex.value + 1 : 0
        jump(new_value)
    }

    const previous = () => {
        const new_value = activeIndex.value > 0 ? activeIndex.value - 1 : 0
        jump(new_value)
    }

    onMounted(() => {
        if (process.server) return;
        if (speakersContainer.value) speakersContainer.value.addEventListener('scroll', calculateVisibility, { passive: true })
        nextTick(() => { calculateVisibility() })
    })

    onUnmounted(() => {
        if (process.server) return;
        if (speakersContainer.value) speakersContainer.value.removeEventListener('scroll', calculateVisibility)
    })

</script>
<template>
    <div class="block block-session-speakers" :class=" { 'has-controls': session.speakers?.length > 1 }" :style="`--arrow-left: ${arrowLeft}px; --arrow-right: ${arrowRight}px;`" v-if="session.speakers?.length > 0">
        <div class="container">
            <h4>Session Speakers</h4>
            <div class='carousel'>
                <ul class='speakers' ref="speakersContainer">
                    <li v-for="(speaker, index) in session.speakers" :key="index" class="speaker" :class="{ 'active': index === activeIndex }" :data-index="index" @click="jump(index)">
                        <MessLink :href="`/speakers/${speaker.id}`" :inert="index !== activeIndex">
                            <SpeakersCard :speaker="speaker" :index=index hide_featured teal />
                        </MessLink>
                    </li>
                    <li class="cheat-right"></li>
                </ul>
                <div class='controls' v-if="session.speakers?.length > 1">
                    <FragmentsCarouselsControlsArrows :total=session.speakers.length :current=activeIndex @forward="next" @back="previous" arrow_type="carouselArrow" />
                    <FragmentsCarouselsControlsDot color="var(--teal)" background="var(--cream-dark)" :itemTotal="session.speakers.length" :activeIndex="activeIndex" @updateIndex="jump" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .block.block-session-speakers {
        overflow: hidden;
        --mobile-width: 90vw;
        --max-width: 63.4375rem;


        h4 {
            margin-bottom: 2.5rem;
            font-size: calc(48 / var(--base-fs) * 1rem);
            font-weight: 500;
            letter-spacing: -0.03rem;
            line-height: 3.375rem;
        }

        &:not(.has-controls) {
            .speakers {
                margin-bottom: 0;
            }
        }
        
        .speakers {
            margin-bottom: 2.3125rem;
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            overflow: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            gap: 1.875rem;
            border-radius: var(--radius);

            @include hide-scrollbar;

            .cheat-right {
                flex: 0 0 100vw;
                max-width: 100vw;
            }

            .speaker {
                display: flex;
	            flex-direction: column;
                transition: opacity 0ms ease-in-out;
                flex: 1 0 var(--mobile-width);
                max-width: var(--mobile-width);
                cursor: pointer;

                @media(min-width: $tablet) {
	                flex: 1 0 var(--max-width);
                    max-width: var(--max-width);
                }

                &.active {
                    opacity: 1;
                    cursor: default;

                    @include hover {
                        .speaker-card {
                            .bio {
                                h3 { color: var(--orange-dark); }
                            }
                            .speaker-image {
                                img { transform: scale(var(--scale)); }
                            }
                        }
                    }
                }

                .speaker-card {
                    height: 100%;

                    .inner {
                        flex-direction: row;
                    }

                    .bio {
                        opacity: var(--inverse-visibility);
                    }

                    @media(max-width: calc($tablet - 1px)) {
                        
                        .featured {
                            font-size: 0.6875rem;
                            letter-spacing: 0.034375rem;
                            font-weight: 500;
                            margin-bottom: 0.4375rem;
                        }
                        
                        h3 {
                            font-size: 1.1875rem;
                            line-height: 1.5625rem;
                            font-weight: 600;
                            margin-bottom: 0;
                        }

                        .job-title {
                            font-size: 0.75rem;
                            line-height: 0.9375rem;
                            font-weight: 500;
                            letter-spacing: 0.0375rem;
                            margin-bottom: 0;
                        }
                        
                        .speaker-image {
                            height: 101px;
                            flex: 0 0 101px;
                        }
                    }

                    .speaker-image {
                        position: relative;
                        overflow: hidden;
                        
                        img {
                            opacity: var(--inverse-visibility);
                            filter: sepia(calc(100% - (100% * var(--inverse-visibility)))) grayscale(calc(100% - (100% * var(--inverse-visibility))));
                        }
                    }
                }
            }
        }


        .controls {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.9375rem;
            justify-content: space-between;
            width: 100%;

            .dot-controls {
                margin-left: auto;
                align-self: center;
                
                @media (hover:hover) {
                    .dot-button:hover {
                        svg { fill: var(--orange-dark); }
                    }
                }
            }

            .back-next {
                gap: 1.625rem;
                transition: left 1s ease-in-out;
                
                svg { color: var(--teal); }
                
                .active:hover {
                    svg { color: var(--orange-dark); }
                }

                @media(min-width: $tablet) {
                    position: absolute;
                    left: var(--arrow-left);
                }
            }
        }

    }
</style>